import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { AiOutlineCheckCircle, AiOutlineCloseCircle } from 'react-icons/ai';
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useEmployeeDetails from '../hooks/useEmployeeDetails';
import { MdKeyboardDoubleArrowLeft } from "react-icons/md";


const UpdatePassword = () => {
    const { id, employeeToken, employeeName } = useEmployeeDetails();
    const API_URL = process.env.REACT_APP_BASE_URL;
    const [formData, setFormData] = useState({
        emp_id: employeeName || '',
        old_password: '',
        new_password: '',
        confirm_password: '',
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        // Prefill emp_id if not set
        if (id && !formData.emp_id) {
            setFormData(prev => ({ ...prev, emp_id: id }));
        }
    }, [id, formData.emp_id]);

    const validate = () => {
        const newErrors = {};
        if (!formData.emp_id.trim()) newErrors.emp_id = 'Employee ID is required';
        if (!formData.old_password.trim()) newErrors.old_password = 'Old Password is required';
        if (!formData.new_password.trim()) newErrors.new_password = 'New Password is required';
        if (!formData.confirm_password.trim()) newErrors.confirm_password = 'Confirm Password is required';
        if (
            formData.new_password &&
            formData.confirm_password &&
            formData.new_password !== formData.confirm_password
        ) {
            newErrors.confirm_password = 'Passwords do not match';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        try {
            setLoading(true);
            const payload = {
                admin_id: formData.emp_id,
                old_password: formData.old_password,
                new_password: formData.new_password,
            };

            const response = await axios.post(`${API_URL}/update_emp_password`, payload, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${employeeToken}`,
                },
            });

            if (response.data.success === true) {
                confetti({
                    particleCount: 150,
                    spread: 100,
                    origin: { y: 0.6 },
                });
                toast.success(response.data.message || "Password updated successfully");
                setTimeout(() => navigate('/login'), 2000);
            } else {
                toast.error(response.data.message || "Failed to update password.");
            }

        } catch (error) {
            toast.error(error.response?.data?.message || "Server error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const isFormValid =
        formData.emp_id &&
        formData.old_password &&
        formData.new_password &&
        formData.confirm_password &&
        formData.new_password === formData.confirm_password;

    return (
        <div className="flex items-center justify-center">
            <form
                onSubmit={handleSubmit}
                className=" p-4 w-full max-w-sm space-y-4"
            >
                {/* 🌐 Circle illustration (visible on all screens) */}
                {/* <img
                    src="/circle.png"
                    alt="Top Illustration"
                    className="absolute top-0 left-0 w-32 h-32 object-contain"
                /> */}
                <div className="relative flex justify-center items-center mb-6">
                    {/* Arrow aligned to left */}
                    <p
                        className="absolute left-0 top-1 text-xl cursor-pointer text-gray-700 hover:text-gray-900"
                        onClick={() => navigate("/sidebar/employeeDetails")}
                    >
                        <MdKeyboardDoubleArrowLeft size={24} />
                    </p>

                    {/* Heading centered */}
                    <h2 className="text-xl font-bold tracking-widest uppercase text-center">
                        Update Password
                    </h2>
                </div>
                {/* 🖼️ Left Side Image */}
                <div className="w-full md:w-1/2 flex justify-center items-center bg-white p-4 md:p-8">
                    <img
                        src="/update.png"
                        alt="Login Illustration"
                        className="w-full h-auto max-h-[200px] md:max-h-[700px] object-contain"
                    />
                </div>
                <p className='uppercase text-md font-bold text-center tracking-wider'>Employee Login</p>

                {/* Employee ID */}
                <div>
                    <input
                        type="text"
                        name="emp_id"
                        placeholder="Employee ID"
                        value={formData.emp_id}
                        onChange={handleChange}
                        readOnly
                        className="w-full p-3 bg-transparent rounded-xl border-2 border-gray-300 pl-6"
                    />
                    {errors.emp_id && <p className="text-red-500 text-xs mt-1">{errors.emp_id}</p>}
                </div>

                {/* Old Password */}
                <div className="relative">
                    <input
                        type={showOldPassword ? 'text' : 'password'}
                        name="old_password"
                        placeholder="Old Password"
                        value={formData.old_password}
                        onChange={handleChange}
                        className="w-full  p-3 bg-transparent rounded-xl border-2 border-gray-300 pr-10 pl-6"
                    />
                    <span
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 cursor-pointer"
                        onClick={() => setShowOldPassword(prev => !prev)}
                    >
                        {showOldPassword ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
                    </span>
                    {errors.old_password && <p className="text-red-500 text-xs mt-1">{errors.old_password}</p>}
                </div>

                {/* New Password */}
                <div className="relative">
                    <input
                        type={showNewPassword ? 'text' : 'password'}
                        name="new_password"
                        placeholder="New Password"
                        value={formData.new_password}
                        onChange={handleChange}
                        className="w-full  p-3 bg-transparent rounded-xl border-2 border-gray-300 pr-10 pl-6"
                    />
                    <span
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 cursor-pointer"
                        onClick={() => setShowNewPassword(prev => !prev)}
                    >
                        {showNewPassword ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
                    </span>
                    {errors.new_password && <p className="text-red-500 text-xs mt-1">{errors.new_password}</p>}
                </div>

                {/* Confirm Password */}
                <div className="relative">
                    <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirm_password"
                        placeholder="Confirm Password"
                        value={formData.confirm_password}
                        onChange={handleChange}
                        className="w-full  p-3 bg-transparent rounded-xl border-2 border-gray-300 pr-10 pl-6"
                    />
                    <span
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 cursor-pointer"
                        onClick={() => setShowConfirmPassword(prev => !prev)}
                    >
                        {showConfirmPassword ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
                    </span>
                    {formData.confirm_password && formData.new_password && (
                        formData.confirm_password === formData.new_password ? (
                            <AiOutlineCheckCircle className="text-green-600 absolute right-10 top-3 text-xl" />
                        ) : (
                            <AiOutlineCloseCircle className="text-red-600 absolute right-10 top-3 text-xl" />
                        )
                    )}
                    {errors.confirm_password && <p className="text-red-500 text-xs mt-1">{errors.confirm_password}</p>}
                </div>

                <button
                    type="submit"
                    className={`w-full py-2 rounded-md font-semibold transition ${isFormValid
                        ? 'bg-[#2E7D32] text-white hover:bg-[#179e9e]'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    disabled={!isFormValid || loading}
                >
                    {loading ? 'Updating...' : 'SUBMIT'}
                </button>
            </form>
        </div>
    );
};

export default UpdatePassword;
